import { select, call, put, delay, takeLatest, cancelled } from "redux-saga/effects";
import {
  createExploreShapesDataObjectInstance, setExplorePropertyDataObjects, createExplorePropertyDataObjectInstance,
  setExploreShapesDataObjects, deleteExploreViewLayer, deleteExploreViewObject, deleteExplorePropertyDataObjectInstance,
  deleteExploreShapesDataObjectInstance,
  fetchExploreViews, createExploreView, createExploreSystemView, saveExploreView, deleteExploreView, setExploreViewsLoadingState, setExploreViews,
  updateExploreViewSharing, setExploreViewSharedWithUsers,
  setExploreSelectedViewId, setExploreSelectedViewIsOpen,
  setExploreSelectedViewLoadingState, setExploreSelectedView, createExploreViewObject, setExploreSelectedViewIsDirty,
  requestExploreSelectedViewIdChange, setExploreSaveViewDialogParameters, updateSelectedExploreView, updateExploreObjectById,
  createExploreViewLayer,
  setExploreRequestedGroupLevel,
  setExplorePinDisplayType,
  setExploreFilter,
  setExploreGeoFilter,
  setPinColorIds,
  setPinColorPaletteId,
  setExploreShapesColoringType,
  setExploreShapesRequestedGeoType,
  updateExploreShapesDataObjectById,
  appNotify,
  cancelExploreDataFetch,
  updateExploreViewsOrder,
  highlightExploreViewObject,
  setExploreHighlightedObjectId,
  setExploreShapesIsVisible,
  setExplorePinsAreVisible,
  handleExploreViewObjectVisibilityChange,
  setExplorePins,
  setIsRedirectToExplore
} from 'store/actions';
import { exploreMapSelector, exploreViewsStateSelector, exploreViewsSelector, exploreSelectedViewSelector } from "store/selectors";
import { getWithToken, postWithToken, putWithToken } from 'api/network';
import { Constants } from 'utilities/constants';
import { sendClientLog } from 'services/logService';
import { createGUID } from 'utilities';
import { initialState } from 'store/explore/map';
import { getObjectDefaults } from 'store/explore/views';
import { customOperatorToDefault } from 'components/explore/views/filterEditorHelpers';

function* fetchViews() {
  try {
      yield put(setExploreViewsLoadingState({ isLoading: true, message: 'Loading views...' }));
      const url = `/api/explore/views/forUser`;
      const response = yield getWithToken(url);
      const data = yield response.json();

      const { sortObject } = yield select(exploreViewsStateSelector)
      const sortedField = sortObject.find(field => field.order);
      const sortedViews = sortedField ? yield call(getSortedViews, data, {
        sortOrder: sortedField.order,
        type: sortedField.type
      }) : data;
      yield put(setExploreViews(sortedViews));
      yield put(setExploreViewsLoadingState({ isLoading: false, message: null }));

  } catch (error) {
    console.log(error);
    yield put(appNotify(error.message, 'error'));
    yield put(setExploreViewsLoadingState({ isLoading: false, message: null }));
  } finally {
    if (yield cancelled()) {
      yield put(setExploreViewsLoadingState({ isLoading: false, message: null }));
    }
  }
}

function* createView({ payload }) { // Client-side only, user must manually save a new view to db
  const { name, privacy, fromExisting } = payload;
  const id = 'temp';
  const { groupLevel, pinDisplayType, pinColorIds, pinColorPaletteId, filter, filterGeo } = getObjectDefaults(Constants.ViewsObjectType.PROPERTYDATA)

  const source = yield select(fromExisting ? exploreSelectedViewSelector : exploreMapSelector);
  const propertyDataObj = {
    id: 'tempDefaultObject0',
    type: Constants.ViewsObjectType.PROPERTYDATA,
    index: 0,
    visible: true,
    description: 'Property Data',
    layerId: 'tempDefaultLayer0',
    groupLevel,
    pinDisplayType,
    pinColorIds,
    pinColorPaletteId,
    filter,
    filterGeo
  }
  
  const newSelectedView = {
    id,
    name,
    description: '',
    privacy,
    zoomLevel: fromExisting ? source.zoomLevel : source.zoom,
    latitude: fromExisting ? source.latitude : source.center.lat,
    longitude: fromExisting ?  source.longitude : source.center.lng,
    bounds: source.bounds,
    useSavedPosition: fromExisting ? source.useSavedPosition : true,
    mapViewType: fromExisting ? source.mapViewType : source.mapType, // TODO: rename old mapType references to mapViewType
    propertyDataDisplay: fromExisting ? source.propertyDataDisplay : { visible: false, selectedSourceId: 'tempDefaultObject0', type: 'Grid', chartsSelectedDistributionType: 'YearBuilt' },
    layers: fromExisting ? source.layers : [{ id: 'tempDefaultLayer0', name: 'New Layer', index: 0, visible: true, objectIds: ['tempDefaultObject0']}],
    objects: fromExisting ? source.objects : [propertyDataObj],
    fromExisting
  }

  function* newViewActions() {
    yield put(setExploreSelectedViewId(id, false, !fromExisting));
    yield put(setExploreSelectedView(newSelectedView));
    if (!fromExisting) {
      yield put(createExplorePropertyDataObjectInstance(propertyDataObj));
    }
  }
  const { selectedViewId, selectedViewIsDirty } = yield select(exploreViewsStateSelector);
  if (selectedViewId && selectedViewIsDirty && !fromExisting) {
    yield put(setExploreSaveViewDialogParameters(true, id, newSelectedView));
  } else {
    yield call(newViewActions);
  }
}

function* createSystemView({ payload }) { // Client-side only, for viewing a certain data set, saving not allowed unless a duplicate is created
  const { name, filter } = payload;
  const id = 'system';
  const { groupLevel, pinDisplayType, pinColorIds, pinColorPaletteId, filterGeo } = getObjectDefaults(Constants.ViewsObjectType.PROPERTYDATA);

  const propertyDataObj = {
    id: 'tempSystemObject0',
    type: Constants.ViewsObjectType.PROPERTYDATA,
    index: 0,
    visible: true,
    description: 'Properties',
    layerId: 'tempSystemLayer0',
    groupLevel,
    pinDisplayType,
    pinColorIds,
    pinColorPaletteId,
    filter,
    filterGeo,
    isFilterSet: true
  }
  const newSelectedView = {
    id,
    name,
    description: '',
    privacy: 'Private',
    zoomLevel: initialState.zoom,
    latitude: initialState.center?.lat,
    longitude: initialState.center?.lng,
    bounds: initialState.bounds,
    useSavedPosition: true,
    mapViewType: initialState.mapType,
    propertyDataDisplay: { visible: false, selectedSourceId: 'tempSystemObject0', type: 'Grid', chartsSelectedDistributionType: 'YearBuilt' },
    layers: [{ id: 'tempSystemLayer0', name: 'Layer 1', index: 0, visible: true, objectIds: ['tempSystemObject0']}],
    objects: [propertyDataObj]
  }

  function* newViewActions() {
    yield put(setExploreSelectedViewId(id, false, true));
    yield put(setExploreSelectedView(newSelectedView));
    yield put(setExplorePins([]));
    yield put(createExplorePropertyDataObjectInstance(propertyDataObj));
    yield put(setExploreSelectedViewIsOpen(true));
    yield put(setIsRedirectToExplore(true));
  }
  const viewsState = yield select(exploreViewsStateSelector);
  if (viewsState.selectedViewId && viewsState.selectedViewIsDirty) {
    yield put(setExploreSaveViewDialogParameters(true, id, newSelectedView));
  } else {
    yield call(newViewActions);
  }
}

function* onRequestedViewIdChange({ payload }) {
  yield put(cancelExploreDataFetch());
  const { id, callback } = payload;
  if (id == null) return;
  const viewsState = yield select(exploreViewsStateSelector);
  if (viewsState.selectedViewId && viewsState.selectedViewIsDirty) {
    console.log('view is dirty')
    yield put(setExploreSaveViewDialogParameters(true, id));
  } else {
    yield put(setExploreSelectedViewId(id));
    callback && callback();
  }
}

function* fetchViewData(selectedViewId) {
  if (selectedViewId == null) return;
  // const data = viewsData.find(view => view.id === selectedViewId);
  // if (!data) return;
  try {
      yield put(setExploreSelectedViewLoadingState({ isLoading: true, message: 'Loading view...' }));
      const url = `/api/explore/views/${selectedViewId}`;
      const response = yield getWithToken(url);
      const data = yield response.json();
      sendClientLog('Info',`Explore Views API - Fetched data for view id ${selectedViewId}`);

      const propertyDataDisplay = JSON.parse(data.propertyDataDisplay);
      if (propertyDataDisplay.clientId) {
        propertyDataDisplay.selectedSourceId = propertyDataDisplay.clientId;
        delete propertyDataDisplay.clientId;
      }
      // yield delay(1000);
      let viewObjects = [];
      const mappedData = {
        ...data,
        bounds: JSON.parse(data.bounds),
        // propertyDataDisplay: data.propertyDataDisplay ? JSON.parse(data.propertyDataDisplay) : { visible: false, selectedSourceId: null, type: 'Grid', chartsSelectedDistributionType: 'YearBuilt' },
        propertyDataDisplay,
        layers: data.layers.map(layer => {
          const { objects, ...rest } = layer;
          viewObjects = viewObjects.concat(objects);
          return {
            ...rest,
            objectIds: objects.map(obj => obj.id)
          }
        })
      }
      mappedData.objects = viewObjects.map(obj => {
        const { objectData, ...rest } = obj;
        return {
          ...rest,
          ...JSON.parse(objectData)
        }
      });
      yield put(setExploreSelectedView(mappedData));
      yield put(setExploreSelectedViewLoadingState({ isLoading: false, message: null }));
      for (let obj of mappedData.objects) {
        // if (obj.type === Constants.ViewsObjectType.PROPERTYDATA) {
        //   console.log('propertyData found')
        //   yield put(createExplorePropertyDataObjectInstance(obj));
        // }
        switch (obj.type) {
          case Constants.ViewsObjectType.PROPERTYDATA:
            yield put(createExplorePropertyDataObjectInstance(obj));
            break;
          case Constants.ViewsObjectType.MARKETS:
            yield put(createExploreShapesDataObjectInstance(obj));
            break;
          default:
            break;
        }
      }
  } catch (error) {
    console.log(error);
    yield put(appNotify(error.message, 'error'));
    yield put(setExploreSelectedViewLoadingState({ isLoading: false, message: null }));
    // The view may have been deleted or un-shared since the list loaded; don't leave the panel open on stale/empty data.
    // setExploreSelectedViewId must be last: it restarts the takeLatest saga this runs inside, cancelling anything after it.
    yield put(setExploreSelectedView(null));
    yield put(setExploreSelectedViewIsOpen(false));
    yield put(fetchExploreViews());
    yield put(setExploreSelectedViewId(null, false));
  } finally {
    if (yield cancelled()) {
      yield put(setExploreSelectedViewLoadingState({ isLoading: false, message: null }));
    }
  }
}

function* updateViewSharing({ payload }) {
  const { viewId, userIds, callback } = payload;
  try {
    const url = `/api/explore/views/${viewId}/sharing`;
    const response = yield putWithToken(url, { userIds });
    const sharedWithUsers = yield response.json();
    sendClientLog('Info',`Explore Views API - Updated sharing for view id ${viewId} (${sharedWithUsers.length} users)`);
    yield put(setExploreViewSharedWithUsers(viewId, sharedWithUsers));
    yield put(appNotify('Sharing updated', 'success'));
    callback && callback(true);
  } catch (error) {
    console.log(error);
    yield put(appNotify(error.message, 'error'));
    callback && callback(false);
  }
}

function* saveView({ payload: callback }) {
  const selectedView = yield select(exploreSelectedViewSelector);
  if (!selectedView) return;
  try {
    const isTempOrSystem = selectedView.id === 'temp' || selectedView.id === 'system';
    const endpoint = isTempOrSystem ? 'create' : 'update';
    const method = isTempOrSystem ? postWithToken : putWithToken;
    // const endpoint = 'create';
    yield put(setExploreSelectedViewLoadingState({ isLoading: true, message: 'Saving view...' }));
    const url = `/api/explore/views/${endpoint}`;
    const shouldOmitId = (id) => { // Don't pass id for new (client-side only) layers/objects, so that new db records will be created
      return isTempOrSystem || (typeof id === 'string' && id.startsWith('temp'))
    }
    const newLayers = selectedView.layers.map(layer => {
      const { id, ...rest } = layer;
      return {
        ...rest,
        id: shouldOmitId(id) ? undefined : id,
        objects: selectedView.objects.filter(obj => obj.layerId === layer.id).map(obj => {
          const { id, layerId, type, index, visible, description, ...rest } = obj;
          if (typeof id === 'string' && id.startsWith('temp')) {
            rest.clientId = id;
          }
          return {
            id: shouldOmitId(id) ? undefined : id,
            layerId: shouldOmitId(layerId) ? undefined : layerId,
            type,
            index,
            visible,
            description,
            objectData: JSON.stringify(rest)
          }
        })
      }
    })
    const { id: viewId, sharedWithUsers, ...rest } = selectedView; // Sharing is managed via its own endpoint, never through create/update
    const shouldOmitViewId = shouldOmitId(viewId);
    const isTempPropertyDataDisplayId = selectedView.propertyDataDisplay.selectedSourceId != null && shouldOmitId(selectedView.propertyDataDisplay.selectedSourceId);
    const pdd = { ...selectedView.propertyDataDisplay }
    if (isTempPropertyDataDisplayId) {
      pdd.clientId = pdd.selectedSourceId;
      delete pdd.selectedSourceId;
    }
    const requestDto = {
      ...rest,
      id: shouldOmitViewId ? undefined: viewId,
      zoomLevel: selectedView.zoomLevel,
      latitude: selectedView.latitude,
      longitude: selectedView.longitude,
      bounds: JSON.stringify(selectedView.bounds),
      propertyDataDisplay: JSON.stringify(pdd),
      layers: JSON.stringify(newLayers)
    }
    const response = yield method(url, requestDto);
    const id = yield response.json();
    sendClientLog('Info',`Explore Views API - ${isTempOrSystem ? 'Created' : 'Updated'} view with id ${id}`);
    // yield delay(1000);
    yield put(setExploreSelectedViewId(id, true));
    yield put(setExploreSelectedViewIsDirty(false));
    yield put(fetchExploreViews());
    yield put(setExploreSelectedViewLoadingState({ isLoading: false, message: null }));
    yield put(appNotify('View saved', 'success'));
    callback && callback();
  } catch (error) {
    console.log(error);
    yield put(appNotify(error.message, 'error'));
    yield put(setExploreSelectedViewLoadingState({ isLoading: false, message: null }));
  } finally {
    if (yield cancelled()) {
      yield put(setExploreSelectedViewLoadingState({ isLoading: false, message: null }));
    }
  }
}

function* deleteView({ payload }) {
  try {
    function* clearSelectedViewActions() {
      yield put(setExploreSelectedViewId(null, false));
      yield put(setExploreSelectedView(null));
      yield put(setExploreSelectedViewIsDirty(false));
    }

    const isTempOrSystem = payload === 'temp' || payload === 'system';
    const selectedView = yield select(exploreSelectedViewSelector);
    if (isTempOrSystem && payload === selectedView?.id) {
      yield call(clearSelectedViewActions);
      return;
    }
    yield put(setExploreSelectedViewLoadingState({ isLoading: true, message: 'Deleting view...' }));
    yield put(setExploreViewsLoadingState({ isLoading: true, message: 'Deleting view...' }));
    const url = `/api/explore/views/${payload}/delete`;
    const response = yield putWithToken(url);
    const id = yield response.json();
    sendClientLog('Info',`Explore Views API - Deleted view with id ${id}`);
    // yield delay(1000);
    // const data = views;
    if (payload === selectedView?.id) {
      yield call(clearSelectedViewActions);
    }
    yield put(appNotify('View deleted', 'success'));
    yield put(setExploreSelectedViewLoadingState({ isLoading: false, message: null }));
    yield put(setExploreViewsLoadingState({ isLoading: false, message: null }));
    yield put(fetchExploreViews());
} catch (error) {
  console.log(error);
  yield put(appNotify(error.message, 'error'));
  yield put(setExploreSelectedViewLoadingState({ isLoading: false, message: null }));
  yield put(setExploreViewsLoadingState({ isLoading: false, message: null }));
} finally {
  if (yield cancelled()) {
    yield put(setExploreSelectedViewLoadingState({ isLoading: false, message: null }));
    yield put(setExploreViewsLoadingState({ isLoading: false, message: null }));
  }
}
}

function* createDataObject({ payload }) {
  const selectedView = yield select(exploreSelectedViewSelector);
  const object = selectedView.objects.find(obj => obj.id === payload.objectId);
  switch (payload.objectType) {
    case Constants.ViewsObjectType.PROPERTYDATA:
      console.log('propertyData found')
      yield put(createExplorePropertyDataObjectInstance(object));
      const selectedView = yield select(exploreSelectedViewSelector);
      if (selectedView.propertyDataDisplay.selectedSourceId == null) {
        yield put(updateSelectedExploreView({ propertyDataDisplay: { ...selectedView.propertyDataDisplay, selectedSourceId: payload.objectId }}, true));
      }
      break;
    case Constants.ViewsObjectType.MARKETS:
      console.log('markets found')
      yield put(createExploreShapesDataObjectInstance(object));
      break;
    default:
      break;
  }
  // yield put(setExploreSelectedViewIsDirty(true));
}

function* deleteViewObject({ payload }) {
  const { layerId, objectId } = payload;
  const selectedView = yield select(exploreSelectedViewSelector);
  const objectToDelete = selectedView.objects.find(obj => obj.id === objectId);

  const otherObjectsInSameLayer = selectedView.objects.filter(obj => obj.id !== objectId && obj.layerId === layerId).sort((a, b) => a.index - b.index).map((obj, index) => {
    return {
      ...obj,
      index
    }
  });
  const newObjects = selectedView.objects.filter(object => object.id !== objectId).map(obj => {
    const updatedObj = otherObjectsInSameLayer.find(el => el.id === obj.id)
    if (updatedObj) {
      return updatedObj;
    }
    return obj;
  });
  const foundLayer = selectedView.layers.find(layer => layer.id === layerId); // Only exists during single object deletion. If object deletion is being called from deleteExploreViewLayer, the layer will already be removed.
  let newLayers = selectedView.layers;
  if (foundLayer) {
    newLayers = newLayers.map(layer => {
      if (layer.id === layerId) {
        return {
          ...layer,
          objectIds: layer.objectIds.filter(id => id !== objectId)
        }
      }
      return layer;
    });
  }
  
  const pdd = {...selectedView.propertyDataDisplay};

  switch (objectToDelete.type) {
    case Constants.ViewsObjectType.PROPERTYDATA:
      console.log('propertyData found')
      yield put(deleteExplorePropertyDataObjectInstance(objectId));
      if (objectId === pdd.selectedSourceId) { // If the object to delete is the PDO grid's selected source, clear it
        pdd.selectedSourceId = null;
      } else if (objectId === pdd.clientId) {
        delete pdd.clientId;
      }
      const hasNoOtherPdos = !selectedView.objects.find(obj => obj.type === Constants.ViewsObjectType.PROPERTYDATA && obj.id !== objectId);
      if (hasNoOtherPdos) {
        pdd.visible = false;
      }
      break;
    case Constants.ViewsObjectType.MARKETS:
      console.log('markets found')
      yield put(deleteExploreShapesDataObjectInstance(objectId));
      break;
    default:
      break;
  }
  // yield put(setExploreSelectedViewIsDirty(true));
  yield put(setExploreSelectedView({ ...selectedView, propertyDataDisplay: pdd, objects: newObjects, layers: newLayers }));
}

function* handleDuplicateLayer({ payload }) {
  if (payload?.idToDuplicate == null) return;
  const viewsState = yield select(exploreViewsStateSelector);
  const newLayerId = viewsState.lastCreatedLayerId;
  const selectedView = yield select(exploreSelectedViewSelector);
  
  const layerToDuplicate = selectedView.layers.find(layer => layer.id === payload.idToDuplicate);
  for (let objectId of layerToDuplicate.objectIds) {
    const object = selectedView.objects.find(object => object.id === objectId);
    const { id, type, index, layerId, clientId, ...rest } = object;
    yield put(createExploreViewObject('tempObject-' + createGUID(), newLayerId, type, { ...rest }));
  }
}

function* getSortedViews(originalViews, sortObj) {
  try {
    let views = originalViews ? [...originalViews] : [];

    function recentlyUsed() {
      views.sort((a, b) => {
        const firstDate = +new Date(a.updatedOn || a.createdOn);
        const secondDate = +new Date(b.updatedOn || b.createdOn);
        if (sortObj.sortOrder === 'asc') {
          return firstDate - secondDate;
        }

        if (sortObj.sortOrder === 'desc') {
          return secondDate - firstDate;
        }

        return 0;
      });
    }

    function dateCreatedSorting() {
      views.sort((a, b) => {
        if (sortObj.sortOrder === 'asc') {
          return (+new Date(a.createdOn)) - (+new Date(b.createdOn));
        }

        if (sortObj.sortOrder === 'desc') {
          return (+new Date(b.createdOn)) - (+new Date(a.createdOn));
        }

        return 0;
      });
    }

    function defaultSorting() {
      views.sort((a, b) => a.id - b.id);
    }

    function alphabeticalSorting() {
      views.sort((a, b) => {
        if (sortObj.sortOrder === 'asc') {
          return a.name.localeCompare(b.name);
        }

        if (sortObj.sortOrder === 'desc') {
          return b.name.localeCompare(a.name);
        }

        return 0;
      });
    }

    switch (sortObj.type) {
      case 'LAST_UPDATED':
        recentlyUsed();
        break;
      case 'DATE_CREATED':
        dateCreatedSorting();
        break;
      case 'ALPHABETICAL':
        alphabeticalSorting();
        break;
      case 'DEFAULT':
        defaultSorting();
        break;
      default:

    }
    return views;
  } catch(error) {
    yield put(appNotify(error.message, 'error'));
  }
}

export function* exploreViewsSaga() {
  yield takeLatest(fetchExploreViews, fetchViews);
  yield takeLatest(setExploreSelectedViewId, function* ({ payload }){
    const { selectedViewId, shouldFetch, shouldClearDataObjects, callback } = payload;
    if (shouldClearDataObjects) {
      yield put(setExplorePropertyDataObjects({}));
      yield put(setExploreShapesDataObjects({}));
    }

    yield put(setExploreSelectedViewIsDirty(selectedViewId === 'temp' || selectedViewId === 'system'));
    if (selectedViewId) {
      yield put(setExploreSelectedViewIsOpen(true));
    }
    if (shouldFetch) {
      yield call(fetchViewData, selectedViewId);
    }
    callback && callback();
  });
  yield takeLatest(createExploreView, createView);
  yield takeLatest(createExploreSystemView, createSystemView);
  yield takeLatest(saveExploreView, saveView);
  yield takeLatest(deleteExploreView, deleteView);
  yield takeLatest(updateExploreViewSharing, updateViewSharing);
  yield takeLatest(requestExploreSelectedViewIdChange, onRequestedViewIdChange);
  yield takeLatest(createExploreViewObject, createDataObject);
  yield takeLatest(deleteExploreViewObject, deleteViewObject);
  yield takeLatest(createExploreViewLayer, handleDuplicateLayer);
  yield takeLatest(deleteExploreViewLayer, function* ({ payload }){
    const selectedView = yield select(exploreSelectedViewSelector);
    const objectsToDelete = selectedView.objects.filter(object => object.layerId === payload);
    for (let obj of objectsToDelete) {
      yield put(deleteExploreViewObject(payload, obj.id));
    }
  })
  yield takeLatest([
    createExploreView,
    createExploreViewObject,
    deleteExploreViewObject,
    updateSelectedExploreView,
    updateExploreObjectById,
    createExploreViewLayer,
    deleteExploreViewLayer,
    setExplorePinDisplayType,

  ], function* ({ payload }){
    if (payload?.shouldNotMarkDirty) return;
    yield put(setExploreSelectedViewIsDirty(true));
  })

  yield takeLatest([ // Update object when Property Data state changes
    setExploreFilter,
    setExploreGeoFilter,
    setExploreRequestedGroupLevel,
    setExplorePinDisplayType,
    setPinColorIds,
    setPinColorPaletteId
  ], function* ({ type, payload }){
    const { objectId, ...rest } = payload;
    const payloadFields = ['filter', 'filterGeo', 'requestedGroupLevel', 'pinDisplayType', 'pinColorIds', 'pinColorPaletteId']; // Fields to update from each action type
    const foundField = payloadFields.find(field => Object.keys(rest).includes(field));
    if (!foundField) return;
    const field = foundField === 'requestedGroupLevel'
      ? 'groupLevel'
      : foundField;
    const value = foundField === 'pinDisplayType'
        ? (payload[foundField]?.dataField ?? null)
        : payload[foundField];
    const update = { [field]: value}
    if (type === 'exploreDataSlice/setExploreFilter' || type === 'exploreDataSlice/setExploreGeoFilter') {
      update.isFilterSet = true;
    }
    yield put(updateExploreObjectById(objectId, update));
  })

  yield takeLatest([ // Update object when Markets state changes
    setExploreShapesColoringType,
    setExploreShapesRequestedGeoType,
    updateExploreShapesDataObjectById
  ], function* ({ type, payload }){
    const { objectId, ...rest } = payload;
    const payloadFields = [
      'shapesColoringType',
      'requestedGeoType',
      'customPalette',
      'fillOpacity',
      'strokeWeight'
    ]; // Fields to update from each action type
    const update = type === 'exploreShapesDataSlice/updateExploreShapesDataObjectById' ? rest.update : rest;
    const foundFields = Object.keys(update).filter(field => payloadFields.includes(field));
    if (!foundFields.length) return;
    const updateObj = {};
    foundFields.forEach(foundField => {
      const name = foundField === 'requestedGeoType'
      ? 'geoLevel'
      : foundField === 'shapesColoringType'
        ? 'shapeLabel'
        : foundField;

      updateObj[name] = update[foundField];
    })
    yield put(updateExploreObjectById(objectId, updateObj));
  })

  // yield takeLatest(createExplorePropertyDataObjectInstance,
  // function* ({ payload }){
  //   const selectedView = yield select(exploreSelectedViewSelector);
  //   if (selectedView.propertyDataDisplay.selectedSourceId == null) {
  //     yield put(updateSelectedExploreView({ propertyDataDisplay: { ...selectedView.propertyDataDisplay, selectedSourceId: payload.id }}, true));
  //   }
  // })

  yield takeLatest(updateExploreViewsOrder, function* ({ payload }) {
    const views = yield select(exploreViewsSelector);
    const sortedViews = yield call(getSortedViews, views, payload);
    yield put(setExploreViews(sortedViews));
  });

  yield takeLatest(highlightExploreViewObject,
    function* ({ payload }){
      yield put(setExploreHighlightedObjectId(payload.id));
      yield delay(1000);
      yield put(setExploreHighlightedObjectId(null));
    }
  )

  yield takeLatest(handleExploreViewObjectVisibilityChange,
    function* ({ payload }){
      const { objectIds, visibility, toggleOthers } = payload;
      if (!Array.isArray(objectIds) || !objectIds.length || visibility == null) return;

      const selectedView = yield select(exploreSelectedViewSelector);

      function* setObjectVisibility(object, visible) {
        if (object.visible !== visible) {
          yield put(updateExploreObjectById(object.id, { visible }));
        }
        if (object.type === Constants.ViewsObjectType.MARKETS) {
          yield put(setExploreShapesIsVisible(object.id, visible));
        }
        if (object.type === Constants.ViewsObjectType.PROPERTYDATA) {
          yield put(setExplorePinsAreVisible(object.id, visible));
        }
      }

      if (toggleOthers) { // Sets any nonincluded ids to the opposite visibility. Used for Fit and Focus feature
        let included = [], allOthers = [];
        selectedView.objects.forEach(obj => {
          const isIncluded = objectIds.includes(obj.id);
          if (isIncluded) {
            included.push(obj)
          } else {
            allOthers.push(obj);
          }
        })

        // Prioritize hiding objects first, showing others second
        if (visibility) {
          for (let object of allOthers) {
            yield call(setObjectVisibility, object, false);
          }
          for (let object of included) {
            yield call(setObjectVisibility, object, true);
          }
        } else {
          for (let object of included) {
            yield call(setObjectVisibility, object, false);
          }
          for (let object of allOthers) {
            yield call(setObjectVisibility, object, true);
          }
        }
      } else {
        for (let id of objectIds) {
          const object = selectedView.objects.find(obj => obj.id === id);
          yield call(setObjectVisibility, object, visibility);
        }
      }
    }
  )
}
